import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translations
import deTranslations from '../locales/de/index';
import enTranslations from '../locales/en/index';
import itTranslations from '../locales/it/index';
import esTranslations from '../locales/es/index';
import frTranslations from '../locales/fr/index';

/**
 * Deep merge two objects
 */
function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
  const result = { ...target };

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key];
      const targetValue = target[key];

      if (
        sourceValue !== undefined &&
        typeof sourceValue === 'object' &&
        sourceValue !== null &&
        !Array.isArray(sourceValue) &&
        typeof targetValue === 'object' &&
        targetValue !== null &&
        !Array.isArray(targetValue)
      ) {
        result[key] = deepMerge(targetValue, sourceValue as Partial<typeof targetValue>);
      } else if (sourceValue !== undefined) {
        result[key] = sourceValue as T[typeof key];
      }
    }
  }

  return result;
}

const baseResources = {
  de: deTranslations,
  en: enTranslations,
  it: itTranslations,
  es: esTranslations,
  fr: frTranslations,
};

/**
 * Apply prerendered i18n overrides to base resources.
 * This is called after the page loads if prerendered locales are available.
 */
async function applyPrerenderedOverrides(): Promise<void> {
  // Only attempt in production builds with prerendering
  if (import.meta.env.DEV) {
    return;
  }

  try {
    // Check if prerendered locales manifest exists
    const manifestResponse = await fetch('/generated/build-manifest.json');
    if (!manifestResponse.ok) {
      return;
    }

    const manifest = await manifestResponse.json();
    const localesFile = manifest.files?.find((f: { path: string }) =>
      f.path.startsWith('locales-merged.')
    );

    if (!localesFile) {
      return;
    }

    // Load the prerendered locales
    const localesResponse = await fetch(`/generated/${localesFile.path}`);
    if (!localesResponse.ok) {
      return;
    }

    const prerenderedLocales = await localesResponse.json();

    // Apply overrides to each locale
    for (const [locale, namespaces] of Object.entries(prerenderedLocales)) {
      if (typeof namespaces !== 'object' || namespaces === null) continue;

      for (const [namespace, translations] of Object.entries(
        namespaces as Record<string, unknown>
      )) {
        if (typeof translations !== 'object' || translations === null) continue;

        // Add the translations to i18next
        i18n.addResourceBundle(
          locale,
          namespace,
          translations,
          true, // deep merge
          true  // overwrite
        );
      }
    }

    console.log(
      `[i18n] Applied prerendered overrides (hash: ${manifest.localesHash || 'unknown'})`
    );
  } catch {
    // Silent fail - prerendered locales are optional
  }
}

const resources = baseResources;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'de',
    defaultNS: 'common',
    debug: import.meta.env.DEV,

    detection: {
      order: ['localStorage', 'htmlTag'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },

    interpolation: {
      escapeValue: false,
    },

    react: {
      useSuspense: false,
    },
  })
  .then(() => {
    // Apply prerendered overrides after i18n is initialized
    void applyPrerenderedOverrides();
  });

export default i18n;